import { motion } from 'framer-motion';
import {
  Power,
  Radio,
  Clock,
  Zap,
  AlertTriangle,
  Wifi,
  WifiOff
} from 'lucide-react';
import { RegulatorState, StateChangeOrigin } from '../../types/protocol';

interface RegulatorStatusProps {
  state: RegulatorState;
  stateOrigin: StateChangeOrigin;
  logicalId: number;
  physicalId: string;
  currentPlan: number;
  currentPhase: string;
  cycleTime: number;
  cyclePosition: number;
  isConnected: boolean;
  lastUpdate: Date;
}

const stateLabels: Record<RegulatorState, string> = {
  [RegulatorState.DISCONNECTED]: 'Desconectado',
  [RegulatorState.OFF]: 'Apagado',
  [RegulatorState.FLASHING]: 'Intermitente',
  [RegulatorState.LOCAL_CONTROL]: 'Control Local',
  [RegulatorState.DEMAND_CONTROL]: 'Por Demanda',
  [RegulatorState.REMOTE_CONTROL]: 'Centralizado',
  [RegulatorState.PHASE_END]: 'Fin de Fase'
};

const stateColors: Record<RegulatorState, string> = {
  [RegulatorState.DISCONNECTED]: 'bg-gray-600',
  [RegulatorState.OFF]: 'bg-gray-800',
  [RegulatorState.FLASHING]: 'bg-amber-500',
  [RegulatorState.LOCAL_CONTROL]: 'bg-blue-500',
  [RegulatorState.DEMAND_CONTROL]: 'bg-purple-500',
  [RegulatorState.REMOTE_CONTROL]: 'bg-green-500',
  [RegulatorState.PHASE_END]: 'bg-cyan-500'
};

const originLabels: Record<StateChangeOrigin, string> = {
  [StateChangeOrigin.ALARM]: 'Alarma',
  [StateChangeOrigin.INITIAL]: 'Inicial',
  [StateChangeOrigin.TIMETABLE]: 'Horario',
  [StateChangeOrigin.CALENDAR]: 'Calendario',
  [StateChangeOrigin.CALIBRATION]: 'Calibración',
  [StateChangeOrigin.LOCAL_TERMINAL]: 'Terminal',
  [StateChangeOrigin.EXTERNAL]: 'Externo',
  [StateChangeOrigin.DEMAND]: 'Demanda'
};

function formatTime(tenths: number): string {
  const seconds = Math.floor(tenths / 10);
  const t = tenths % 10;
  return `${seconds}.${t}s`;
}

function CycleProgress({
  cycleTime,
  cyclePosition
}: {
  cycleTime: number;
  cyclePosition: number;
}) {
  const progress = cycleTime > 0 ? (cyclePosition / cycleTime) * 100 : 0;

  return (
    <div className="w-full">
      <div className="flex justify-between text-xs text-gray-400 mb-1">
        <span>Ciclo</span>
        <span>{formatTime(cyclePosition)} / {formatTime(cycleTime)}</span>
      </div>
      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-blue-500"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.1 }}
        />
      </div>
    </div>
  );
}

export function RegulatorStatus({
  state,
  stateOrigin,
  logicalId,
  physicalId,
  currentPlan,
  currentPhase,
  cycleTime,
  cyclePosition,
  isConnected,
  lastUpdate
}: RegulatorStatusProps) {
  const isFlashing = state === RegulatorState.FLASHING;

  return (
    <div className="bg-gray-900 rounded-lg border border-gray-700 p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Radio className="w-5 h-5 text-gray-400" />
          <div>
            <div className="font-mono text-sm text-white">ID: {logicalId}</div>
            <div className="font-mono text-xs text-gray-500">{physicalId}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isConnected ? (
            <Wifi className="w-4 h-4 text-green-500" />
          ) : (
            <WifiOff className="w-4 h-4 text-red-500" />
          )}
        </div>
      </div>

      {/* State Badge */}
      <div className="flex items-center gap-2">
        <motion.div
          className={`px-3 py-1 rounded-full text-sm font-medium text-white ${stateColors[state]}`}
          animate={isFlashing ? { opacity: [1, 0.5, 1] } : { opacity: 1 }}
          transition={isFlashing ? { duration: 1, repeat: Infinity } : {}}
        >
          {stateLabels[state]}
        </motion.div>
        <span className="text-xs text-gray-500">
          ({originLabels[stateOrigin]})
        </span>
      </div>

      {/* Plan and Phase */}
      {state !== RegulatorState.DISCONNECTED && state !== RegulatorState.OFF && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-800 rounded p-2">
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <Zap className="w-3 h-3" />
              Plan
            </div>
            <div className="text-lg font-mono text-white">
              {currentPlan === 0 ? 'EXT' : currentPlan}
            </div>
          </div>
          <div className="bg-gray-800 rounded p-2">
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Fase
            </div>
            <div className="text-lg font-mono text-white">{currentPhase}</div>
          </div>
        </div>
      )}

      {/* Cycle Progress */}
      {cycleTime > 0 && state === RegulatorState.REMOTE_CONTROL && (
        <CycleProgress cycleTime={cycleTime} cyclePosition={cyclePosition} />
      )}

      {/* Last Update */}
      <div className="text-xs text-gray-600 text-right">
        Actualizado: {lastUpdate.toLocaleTimeString()}
      </div>
    </div>
  );
}
